(() => {
  if (customElements.get('thxsilk-gift-progress')) return;
  customElements.define('thxsilk-gift-progress', class extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      if (!this.querySelector('[data-gift-config]')) {
        this.pendingObserver?.disconnect();
        this.pendingObserver = new MutationObserver(() => {
          if (this.querySelector('[data-gift-config]')) {
            this.pendingObserver.disconnect(); this.connectedCallback();
          }
        });
        this.pendingObserver.observe(this, {childList:true, subtree:true});
        return;
      }
      this.active = true;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.track = this.querySelector('.tg-cards-container');
      this.cards = [...this.querySelectorAll('.js-dynamic-card')];
      this.tiers = this.cards.map(card => ({card, cents: Math.round(Number(card.dataset.threshold) * 100), name: card.dataset.name})).filter(t => t.cents > 0).sort((a,b) => a.cents-b.cents);
      this.config = JSON.parse(this.querySelector('[data-gift-config]').textContent);
      this.dots = this.querySelector('.tg-dots');
      this.buttons = this.cards.map((card,i) => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'tg-dot';
        button.setAttribute('aria-label', `Show gift ${i+1} of ${this.cards.length}`);
        button.addEventListener('click', () => {
          this.track.scrollTo({left: card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        }, {signal});
        this.dots.append(button); return button;
      });
      this.dots.hidden = this.buttons.length < 2;
      const syncDots = () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.updateDots()); };
      this.track.addEventListener('scroll', syncDots, {passive:true, signal});
      window.addEventListener('resize', syncDots, {signal});
      this.cartRevision = 0;
      this.schedule = (event) => {
        const cart = event?.cart || event?.detail?.cart;
        // Quantity-change events can arrive out of order; re-read current
        // cart state for those instead of trusting an older response snapshot.
        if (event?.source !== 'cart-items' && this.applyCart(cart)) {
          this.cartRevision++;
          clearTimeout(this.debounce);
          this.refreshPending = false;
          return;
        }
        if (!this.visible || document.hidden) return;
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.refresh(), 300);
      };
      // Theme cart updates use pub/sub, rather than document CustomEvents.
      const connectCartEvents = () => {
        if (this.cartUnsubscribe || !this.isConnected) return;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.schedule);
        }
      };
      connectCartEvents();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', connectCartEvents, {once:true, signal});
      }
      ['cart:updated','cart:refresh','cart-update','cart:change','product:added'].forEach(name => document.addEventListener(name, this.schedule, {signal}));
      window.addEventListener('pageshow', this.schedule, {signal});
      window.addEventListener('focus', this.schedule, {signal});
      document.addEventListener('visibilitychange', () => {if (!document.hidden) this.schedule();}, {signal});
      this.observer = new IntersectionObserver(entries => {this.visible = entries[0].isIntersecting; if(this.visible) this.schedule();});
      this.observer.observe(this);
      this.timer = setInterval(() => {if(this.visible && !document.hidden) this.refresh();}, 60000);
      this.render(this.config.cents, this.config.currency);
      this.updateDots();
    }
    disconnectedCallback() {
      this.pendingObserver?.disconnect();
      this.cartUnsubscribe?.(); this.cartUnsubscribe = null;
      this.refreshPending = false;
      this.active = false; this.events?.abort(); this.request?.abort(); this.observer?.disconnect();
      clearTimeout(this.debounce); clearInterval(this.timer); cancelAnimationFrame(this.frame);
      this.dots?.replaceChildren();
    }
    async refresh() {
      if (!this.isConnected || !this.visible || document.hidden) return;
      if (this.loading) { this.refreshPending = true; return; }
      const cartRevision = this.cartRevision;
      this.loading = true; this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 8000);
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {cache:'no-store', credentials:'same-origin', signal:this.request.signal});
        if(!response.ok) return;
        const cart = await response.json();
        if (this.isConnected && this.cartRevision === cartRevision) this.applyCart(cart);
      } catch(error) { /* Preserve the last valid state on network failure. */ }
      finally {
        clearTimeout(timeout); this.loading = false;
        if (this.refreshPending && this.isConnected) {
          this.refreshPending = false; this.schedule();
        }
      }
    }
    applyCart(cart) {
      if (!cart || !Array.isArray(cart.items) || !Number.isFinite(cart.total_price) || typeof cart.currency !== 'string') return false;
      const giftCardCents = cart.items.filter(item => item.gift_card).reduce((sum, item) => {
        const allocations = item.discount_allocations;
        const net = Array.isArray(allocations)
          ? Number(item.original_line_price) - allocations.reduce((n, allocation) => n + Number(allocation.amount || 0), 0)
          : Number(item.final_line_price);
        return sum + Math.max(0, Number(net) || 0);
      }, 0);
      this.render(Math.max(0, cart.total_price - giftCardCents), cart.currency);
      return true;
    }
    render(cents,currency) {
      const message = this.querySelector('.js-dynamic-message');
      const progress = this.querySelector('.tg-progress-track');
      const valid = currency === 'USD' && this.tiers.length > 0 && Number.isFinite(Number(cents));
      progress.hidden = !valid;
      this.cards.forEach(card=>card.classList.remove('unlocked'));
      if(!valid) {
        message.textContent = this.tiers.length ? 'Gift thresholds are shown in USD. Eligibility is confirmed at checkout.' : 'Gift details will be available soon.';
        return;
      }
      let current = null;
      const next = this.tiers.find(tier=>tier.cents > cents);
      this.tiers.forEach(tier=>{if(cents >= tier.cents) current = tier;});
      if(current) current.card.classList.add('unlocked');
      const pct = Math.min(100,Math.max(0,Number(cents)/this.tiers.at(-1).cents*100));
      this.querySelector('.js-dynamic-bar').style.setProperty('width',pct+'%','important');
      this.querySelectorAll('.js-dynamic-node').forEach(node=>node.classList.toggle('is-active', cents >= Math.round(Number(node.dataset.threshold)*100)));
      const parts = [];
      if(current) parts.push('Your current gift tier: '+current.name+'.');
      if(next) {
        const amount = new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format((next.cents-cents)/100);
        parts.push('Add '+amount+(current ? ' more to reach the next gift tier.' : ' more to reach your first gift tier.'));
      } else if(current) parts.push(this.config.allUnlocked || "You've reached the highest gift tier.");
      const text = parts.join(' ');
      if(message.textContent !== text) message.textContent = text;
    }
    updateDots() {
      if(!this.cards.length) return;
      const max = this.track.scrollWidth-this.track.clientWidth;
      let active = 0, nearest = Infinity;
      if(max > 1 && this.track.scrollLeft >= max-2) active=this.cards.length-1;
      else this.cards.forEach((card,i)=>{
        const distance=Math.abs(card.getBoundingClientRect().left-this.track.getBoundingClientRect().left);
        if(distance < nearest){nearest=distance;active=i;}
      });
      this.buttons.forEach((button,i)=>button.setAttribute('aria-current',String(i===active)));
    }
  });
})();
